// The page planner for the Laporan PDFs: one set of page geometry, one row
// height, one chunker, for every report document. It plans by MEASURING
// (src/pdf/textMetrics.ts gives the widths and line breaks react-pdf itself
// produces), so the row picker in the main bundle can count the pages a
// document will have before the lazy pdf chunk is even loaded, and the
// document then prints exactly that plan.
//
// Pure on purpose: NO react-pdf import, nothing from ./components or the
// document files. Every number here is a style constant from components.tsx
// or a font line height; the planner's answer is the layout's answer.
import { ColumnKind } from "shared/reports/columnCatalogue";
import { TableSortState } from "shared/types/tableSort";
import { DEFAULT_PDF_ROWS } from "./layout";
import { SortMeta, sortRows } from "./sort";
import { Face, SOFT_HYPHEN, TextMeasurer } from "./textMetrics";
import { PAGE } from "./theme";

// ── Column and document contracts ───────────────────────────────────────────

export type PdfColumn<Row> = {
  header: string;
  // The column's share of the row. The report planner sets it to the
  // column's measured inner width (fitColumns below), so the flex shares
  // and the widths are the same numbers; the letters pass their own shares.
  flex: number;
  // Default right (the letters' ledgers); every report column passes left.
  align?: "left" | "right";
  render: (row: Row) => string;
  // One muted second line under the value (body rows only).
  sub?: (row: Row) => string | null;
  // The catalogue kind: decides whether the column may wrap (names and
  // text) or must stay on one line (numbers, money, dates, codes, chips).
  kind?: ColumnKind;
};

export type KpiItem = { value: string; label: string };

// What a report document is made of, declared once so the document and the
// picker read the same thing. `layout` is fixed per report: a "summary"
// document gives page 1 to its KPI strip and charts and starts the table on
// page 2; a "detail" document starts the table under the KPI strip.
export type DocSpec<Data, Row extends Record<string, any>> = {
  layout: "detail" | "summary";
  // Per-render thunk: translate() must run per document, not at module load.
  meta: () => SortMeta;
  // The array the document prints (data.table, data[which], the trend...).
  table: (data: Data) => Row[];
  columns: (data: Data) => PdfColumn<Row>[];
  // Items of the KPI strip in order (empty for a document with none).
  kpis: (data: Data) => KpiItem[];
  // The sort the table opens on; also the caption's fallback. Absent for a
  // document that prints no sort caption (Ringkasan, Bandingkan).
  defaultSort?: { key: string; order: "ascend" | "descend" };
  // Whether the row picker limits the rows (maxRows); false for Ringkasan
  // and Bandingkan, which print everything they are given.
  limited: boolean;
  // Points of extra content between the KPI strip and the table on a detail
  // document's first page (Performa Kelas's session split bar, block.barsRow).
  extrasPt?: (data: Data, block: ReturnType<typeof blockHeights>) => number;
  // Points the summary page's own content takes under the KPI strip (the
  // chart row and any note) when its charts are `chartH` tall. With it the
  // planner can put a short table on page 1, under the charts, instead of
  // giving it a page of its own, and can shorten the charts to make that
  // possible (DocPlan.summaryChartH).
  summaryPt?: (
    data: Data,
    block: ReturnType<typeof blockHeights>,
    m: TextMeasurer,
    chartH: number,
  ) => number;
  // The truncation note text, or undefined when every row is printed.
  note: (
    data: Data,
    shown: number,
    loaded: number,
    sortedOffDefault: boolean,
  ) => string | undefined;
  // A totals row printed under the last page's rows (Ringkasan).
  totalRow?: (data: Data) => Row;
};

// ── Geometry (A4 landscape, PAGE margins from theme.ts) ─────────────────────

export const REPORT_PAGE = {
  textW: 841.89 - PAGE.paddingHorizontal * 2,
  textH: 595.28 - PAGE.paddingTop - PAGE.paddingBottom,
};

// The letters (payslip, invoice, receipt, statement) print A4 portrait.
export const LETTER_PAGE = {
  textW: 595.28 - PAGE.paddingHorizontal * 2,
  textH: 841.89 - PAGE.paddingTop - PAGE.paddingBottom,
};

// Charts on a summary page: two side by side, taller than the screen's
// proportion so the page reads balanced.
export const CHART_GAP = 14;
export const CHART_W_HALF = (REPORT_PAGE.textW - CHART_GAP) / 2;
export const SUMMARY_CHART_H = 200;
// The height the summary charts give way to when that is what lets a short
// table share their page (the letters' chart height). The planner tries the
// full height first.
export const SUMMARY_CHART_H_COMPACT = 150;

// Line heights per role, from the real font metrics (size x 1.21 for all
// four faces), computed once per measurer.
// A summary table goes under the charts only with this much room to spare,
// over the slack every page carries, so a chart measured a hair short can
// never push the last row onto an unplanned page.
export const SUMMARY_FIT_MARGIN_PT = 6;

export const lineHeights = (m: TextMeasurer) => ({
  eyebrow: m.lineHeight("Inter-600", 7),
  title: m.lineHeight("InterTight-600", 23),
  studio: m.lineHeight("Inter-500", 9),
  meta: m.lineHeight("Inter-400", 7.5),
  kpiValue: m.lineHeight("InterTight-600", 19),
  kpiLabel: m.lineHeight("Inter-600", 6.8),
  section: m.lineHeight("InterTight-600", 11.5),
  caption: m.lineHeight("Inter-400", 7.5),
  header: m.lineHeight("Inter-600", 6.8),
  body: m.lineHeight("Inter-400", 8.5),
  sub: m.lineHeight("Inter-400", 7.5),
  runningTitle: m.lineHeight("InterTight-600", 10),
  note: m.lineHeight("Inter-400", 7.5),
  chartTitle: m.lineHeight("InterTight-600", 10),
});
export type LineHeights = ReturnType<typeof lineHeights>;

// Fixed block heights. PdfHeader: eyebrow row, then the title row (title +
// studio on the left; a header stat on the right is shorter than that
// stack, so it adds nothing), its bottom padding and 2pt rule, then the
// period / generated line. PdfRunningHeader: the 10pt title line, padding,
// hairline, margin. PdfSection: 20pt margin + the title line; the sort
// caption adds a line. PdfTable: 8pt margin + 2pt rule above the header
// row. Performa Kelas's split bars: chartRow margin, chart head, 10pt bar,
// caption.
export const blockHeights = (m: TextMeasurer) => {
  const lh = lineHeights(m);
  const chartHead = lh.chartTitle + 6 + 1 + 7;
  return {
    header: Math.max(5.5, lh.eyebrow) + 8 + lh.title + 5 + lh.studio + 14 + 2 + 7 + lh.meta,
    runningHeader: lh.runningTitle + 8 + 1 + 12,
    section: 20 + Math.max(5, lh.section),
    caption: 4 + lh.caption,
    tableTop: 8 + 2,
    chartHead,
    barsRow: 18 + chartHead + 10 + 6 + lh.caption,
  };
};

// A summary page's chart row: 18pt margin, the chart head, the chart.
export const summaryChartRowPt = (block: BlockHeights, chartH: number): number =>
  18 + block.chartHead + chartH;
export type BlockHeights = ReturnType<typeof blockHeights>;

// react-pdf moves a wrap={false} row with zero tolerance; a few points of
// slack per page absorb float rounding across a page of rows.
export const PAGE_SLACK_PT = 3;

// Cell padding (components.tsx td: paddingHorizontal 6, paddingVertical 5)
// and the 1pt rule under each row.
const CELL_PAD_X = 12;
const ROW_PAD_PT = 5 + 5 + 1;

const upper = (s: string) => s.toUpperCase();

// ── Column widths from content ──────────────────────────────────────────────
//
// Every report column is as wide as its header and its widest value need,
// measured with the real faces; spare width is shared equally so a short
// table still spans the page. Headers stay on one line while there is room;
// when there is not, a long header takes two lines before any name is made
// to wrap. Only names and text may wrap: their need is capped, and when the
// one-line columns leave less than the capped needs they give way first,
// down to a floor, so a report's numbers, dates and codes stay on one line
// and its names take the wrap. This replaced the
// kind-based percentage shares, which gave the name column a quarter of the
// page whatever its names needed while the columns beside it wrapped their
// headers and split their dates.

const HEADER_FACE: Face = "Inter-600";
const HEADER_SIZE = 6.8;
const HEADER_SPACING = 0.5;
const BODY_FACE: Face = "Inter-400";
const BODY_SIZE = 8.5;
const SUB_SIZE = 7.5;

const WRAPPING_KINDS: ColumnKind[] = ["name", "text", "twoLine"];
const WRAP_CAP: Partial<Record<ColumnKind, number>> = { name: 200, text: 160, twoLine: 160 };
const WRAP_MIN: Partial<Record<ColumnKind, number>> = { name: 90, text: 70, twoLine: 70 };

const mayWrap = <Row>(c: PdfColumn<Row>) => !!c.kind && WRAPPING_KINDS.includes(c.kind);

// The width a header needs on two lines: its best split at a space (the
// narrower of the two halves' wider one), or its one-line width when it is
// a single word.
const twoLineHeaderWidth = (m: TextMeasurer, header: string): number => {
  const words = header.split(" ");
  let best = m.width(header, HEADER_FACE, HEADER_SIZE, HEADER_SPACING);
  for (let i = 1; i < words.length; i += 1) {
    const left = m.width(words.slice(0, i).join(" "), HEADER_FACE, HEADER_SIZE, HEADER_SPACING);
    const right = m.width(words.slice(i).join(" "), HEADER_FACE, HEADER_SIZE, HEADER_SPACING);
    best = Math.min(best, Math.max(left, right));
  }
  return best;
};

// The inner (text) width each column gets, in order. Pure arithmetic over
// measured needs; the planner caches nothing here because the measurer does.
export const measureColumnWidths = <Row>(
  m: TextMeasurer,
  columns: PdfColumn<Row>[],
  rows: Row[],
  textW = REPORT_PAGE.textW,
): number[] => {
  const cellNeeds = columns.map((c) => {
    let need = 0;
    rows.forEach((row) => {
      need = Math.max(need, m.width(c.render(row), BODY_FACE, BODY_SIZE, 0));
      const sub = c.sub?.(row);
      if (sub) need = Math.max(need, m.width(sub, BODY_FACE, SUB_SIZE, 0));
    });
    return need;
  });
  const headerOneLine = columns.map((c) => m.width(upper(c.header), HEADER_FACE, HEADER_SIZE, HEADER_SPACING));
  // A hair over the measurement: a cell's text must never sit exactly on
  // its edge, where float rounding could wrap it.
  const needsWith = (headers: number[]) => cellNeeds.map((need, i) => Math.max(need, headers[i]) + 0.5);
  const cap = (needs: number[]) =>
    needs.map((need, i) => {
      const kind = columns[i].kind;
      return mayWrap(columns[i]) ? Math.min(need, WRAP_CAP[kind as ColumnKind] ?? need) : need;
    });
  const free = textW - CELL_PAD_X * columns.length;
  const sum = (ws: number[]) => ws.reduce((total, w) => total + w, 0);
  const withAir = (ws: number[]) => {
    const air = (free - sum(ws)) / columns.length;
    return ws.map((w) => w + air);
  };
  // Every header on one line when there is room for that.
  let capped = cap(needsWith(headerOneLine));
  if (sum(capped) <= free) return withAir(capped);
  // Otherwise a long header may take two lines before any name has to wrap:
  // "Rata-rata Waktu Habis Kredit" over a column of "21 hari" costs the
  // names beside it more than a second header line costs the reader.
  capped = cap(needsWith(columns.map((c) => twoLineHeaderWidth(m, upper(c.header)))));
  const total = sum(capped);
  if (total <= free) return withAir(capped);
  // Not enough room for every capped need: the wrapping columns give way,
  // in proportion to how far each stands above its floor.
  const room = capped.map((w, i) =>
    mayWrap(columns[i]) ? Math.max(0, w - (WRAP_MIN[columns[i].kind as ColumnKind] ?? 0)) : 0,
  );
  const roomTotal = room.reduce((sum, r) => sum + r, 0);
  const deficit = total - free;
  if (roomTotal >= deficit) {
    return capped.map((w, i) => w - (deficit * room[i]) / roomTotal);
  }
  // Even at their floors the wrapping columns cannot absorb it. The one-line
  // columns keep their needs (an amount that has to wrap, or run into the
  // next cell, is worse than a name on three lines), and the wrapping
  // columns go below their floors, down to a hard minimum. A report that
  // gets here needs a two-line design (as Pembelian has), not a width rule.
  const HARD_MIN = 40;
  const floored = capped.map((w, i) => w - room[i]);
  const below = floored.map((w, i) => (mayWrap(columns[i]) ? Math.max(0, w - HARD_MIN) : 0));
  const belowTotal = sum(below);
  const left = deficit - roomTotal;
  if (belowTotal >= left) {
    return floored.map((w, i) => w - (left * below[i]) / belowTotal);
  }
  // The one-line columns alone do not fit the page: everything shrinks in
  // proportion and the planner measures what wraps or overflows.
  const hard = floored.map((w, i) => w - below[i]);
  const hardTotal = sum(hard);
  return hard.map((w) => (w * free) / hardTotal);
};

// Soft hyphens inside any word wider than `innerWidth`, placed so each part
// (plus the 5pt hyphen the breaker charges) fits the column. The hyphenation
// callback in fonts.ts splits a word at them and nowhere else; textkit draws
// a hyphen where a line ends at one. A word that fits is returned untouched,
// so almost every cell passes through unchanged.
export const breakLongWords = (
  m: TextMeasurer,
  text: string,
  face: Face,
  size: number,
  innerWidth: number,
): string => {
  const limit = innerWidth - 5.5;
  if (limit <= 0) return text;
  return text
    .split(" ")
    .map((word) => {
      // An amount ("Rp 1.102.500", joined by a no-break space) is one unit
      // and is never cut; its column is always sized to hold it.
      if (word.length < 2 || word.includes("\u00A0") || m.width(word, face, size, 0) <= limit) return word;
      const chars = Array.from(word);
      let out = "";
      let part = "";
      chars.forEach((ch) => {
        const next = part + ch;
        if (part.length > 0 && m.width(next, face, size, 0) > limit) {
          out += part + SOFT_HYPHEN;
          part = ch;
        } else {
          part = next;
        }
      });
      return out + part;
    })
    .join(" ");
};

// The report columns sized to their content: flex = inner width (so
// columnInnerWidths returns these very numbers), and render / sub wrapped
// so a word wider than its column breaks at the column edge.
export const fitColumns = <Row>(
  m: TextMeasurer,
  columns: PdfColumn<Row>[],
  rows: Row[],
  textW = REPORT_PAGE.textW,
): PdfColumn<Row>[] => {
  const inner = measureColumnWidths(m, columns, rows, textW);
  return columns.map((c, i) => {
    const render = (row: Row) => breakLongWords(m, c.render(row), BODY_FACE, BODY_SIZE, inner[i]);
    const sub = c.sub
      ? (row: Row) => {
          const value = c.sub?.(row) ?? null;
          return value === null ? null : breakLongWords(m, value, BODY_FACE, SUB_SIZE, inner[i]);
        }
      : undefined;
    return { ...c, flex: inner[i], render, ...(sub ? { sub } : {}) };
  });
};

// Inner (text) width of each column. react-pdf's `flex: n` is flex-grow n
// on a zero basis, and Yoga keeps each cell's padding outside that basis, so
// a cell is its 12pt padding plus its share of the width left once every
// cell's padding is taken out (measured on rendered output 2026-10-01:
// 100.2 / 157.4 / 88.9pt for shares 13.4 / 22.1 / 11.7 of 753.89 with eight
// columns, which only this rule reproduces).
export const columnInnerWidths = <Row>(columns: PdfColumn<Row>[], textW = REPORT_PAGE.textW): number[] => {
  const total = columns.reduce((sum, c) => sum + c.flex, 0);
  const free = textW - CELL_PAD_X * columns.length;
  return columns.map((c) => (free * c.flex) / total);
};

// The header row: 6pt padding above and below, 1pt rule, the tallest header
// (uppercase, Inter 600 at 6.8pt with 0.5pt letter spacing).
export const tableHeaderPt = <Row>(m: TextMeasurer, lh: LineHeights, columns: PdfColumn<Row>[], inner: number[]): number => {
  const lines = columns.reduce(
    (most, c, i) => Math.max(most, m.lines(upper(c.header), "Inter-600", 6.8, 0.5, inner[i])),
    1,
  );
  return 6 + 6 + 1 + lh.header * lines;
};

// One body row: as tall as its tallest cell (value lines + sub lines).
export const rowPt = <Row>(
  m: TextMeasurer,
  lh: LineHeights,
  row: Row,
  columns: PdfColumn<Row>[],
  inner: number[],
  face: Face = "Inter-400",
  // The totals row prints the value alone (PdfTable's totals branch has no
  // sub Text), so it must not be measured with one.
  withSubs = true,
): number => {
  let tallest = 0;
  columns.forEach((c, i) => {
    const sub = withSubs ? c.sub?.(row) : null;
    // PdfTable prints a sub line inside the same Text, after a newline; that
    // ends the value's paragraph with a hyphen penalty, so the value can wrap
    // a little earlier than its letters alone would (see TextMeasurer.lines).
    const value = m.lines(c.render(row), face, 8.5, 0, inner[i], !!sub) * lh.body;
    const subPt = sub ? m.lines(sub, "Inter-400", 7.5, 0, inner[i]) * lh.sub : 0;
    tallest = Math.max(tallest, value + subPt);
  });
  return ROW_PAD_PT + tallest;
};

// The KPI strip: 18pt margin, then the tallest tile (value lines, 6pt gap,
// label lines). Every tile is `flex: 1` on a zero basis with its padding and
// divider outside that basis (the same Yoga rule as the table cells), so the
// first tile's 20pt of padding and the others' 41pt (20 + 20 + a 1pt
// divider) come off the row first and the remainder is split equally: every
// tile's text width is the same.
export const kpiStripPt = (m: TextMeasurer, lh: LineHeights, items: KpiItem[], textW = REPORT_PAGE.textW): number => {
  if (items.length === 0) return 0;
  const interior = (textW - 20 - 41 * (items.length - 1)) / items.length;
  const tallest = items.reduce((most, item) => {
    const value = m.lines(item.value, "InterTight-600", 19, -0.4, interior) * lh.kpiValue;
    const label = m.lines(upper(item.label), "Inter-600", 6.8, 0.8, interior) * lh.kpiLabel;
    return Math.max(most, value + 6 + label);
  }, 0);
  return 18 + tallest;
};

// PdfNote: 9pt margin + the wrapped text.
export const notePt = (m: TextMeasurer, lh: LineHeights, text: string, textW = REPORT_PAGE.textW): number =>
  9 + m.lines(text, "Inter-400", 7.5, 0, textW) * lh.note;

// ── Chunking ────────────────────────────────────────────────────────────────

// Splits rows into pages by height. A page always takes at least one row;
// zero rows give one page with an empty table (so the document always has a
// page). `tailPt` (the note and/or the totals row) must fit on the LAST page,
// so trailing rows move on until it does.
export const planTablePages = <Row>(
  rows: Row[],
  heights: number[],
  firstAvailPt: number,
  restAvailPt: number,
  tailPt: number,
): Row[][] => {
  if (rows.length === 0) return [[]];
  const pages: number[][] = [[]];
  let used = 0;
  rows.forEach((_, i) => {
    const avail = pages.length === 1 ? firstAvailPt : restAvailPt;
    if (used + heights[i] > avail && pages[pages.length - 1].length > 0) {
      pages.push([]);
      used = 0;
    }
    pages[pages.length - 1].push(i);
    used += heights[i];
  });
  if (tailPt > 0) {
    let avail = pages.length === 1 ? firstAvailPt : restAvailPt;
    while (used + tailPt > avail && pages[pages.length - 1].length > 1) {
      const moved = pages[pages.length - 1].pop() as number;
      pages.push([moved]);
      used = heights[moved];
      avail = restAvailPt;
    }
  }
  return pages.map((page) => page.map((i) => rows[i]));
};

// ── The document plan ───────────────────────────────────────────────────────

export type DocPlan<Row> = {
  // Table pages in order; for a summary document page 1 is the summary and
  // pages[0] prints on page 2 (unless the table is empty, see below).
  pages: Row[][];
  columns: PdfColumn<Row>[];
  kpis: KpiItem[];
  note?: string;
  totalRow?: Row;
  // The table prints under the charts on page 1 of a summary document: an
  // empty table always, a short one when it fits whole.
  tableOnSummaryPage: boolean;
  // The height the summary charts print at: SUMMARY_CHART_H, or the compact
  // height when that is what let the table onto page 1.
  summaryChartH: number;
  pageCount: number;
};

// The row count a request really asks for. NaN means a caller built the
// number wrong (a missing server total divided into a Math.min, say); the
// default is a better answer than an empty table over rows that exist.
// Infinity keeps its plain meaning of "no limit".
export const requestedRows = (maxRows?: number): number => {
  if (maxRows === undefined || Number.isNaN(maxRows)) return DEFAULT_PDF_ROWS;
  return Math.max(1, Math.floor(maxRows));
};

const limitRows = <Row>(rows: Row[], limited: boolean, maxRows?: number): Row[] =>
  limited ? rows.slice(0, requestedRows(maxRows)) : rows;

// A planner for one document over one data set and sort: the sorted rows,
// columns and row heights are computed once, then any row count can be
// planned or merely counted (the picker calls count() on every keystroke).
export const createDocPlanner = <Data, Row extends Record<string, any>>(
  spec: DocSpec<Data, Row>,
  data: Data,
  sortState: TableSortState | null | undefined,
  m: TextMeasurer,
) => {
  const lh = lineHeights(m);
  const block = blockHeights(m);
  const sorted = sortRows(spec.table(data), spec.meta(), sortState ?? null);
  // Widths from every loaded row, so a row choice never changes them and
  // the picker's plan is the document's.
  const columns = fitColumns(m, spec.columns(data), sorted);
  const inner = columnInnerWidths(columns);
  const kpis = spec.kpis(data);
  const headerPt = tableHeaderPt(m, lh, columns, inner);
  const sectionPt = block.section + (spec.defaultSort ? block.caption : 0);
  const common = block.tableTop + headerPt + PAGE_SLACK_PT;
  const restAvail = REPORT_PAGE.textH - block.runningHeader - common;
  const firstAvail =
    spec.layout === "detail"
      ? REPORT_PAGE.textH -
        block.header -
        kpiStripPt(m, lh, kpis) -
        (spec.extrasPt?.(data, block) ?? 0) -
        sectionPt -
        common
      : REPORT_PAGE.textH - block.runningHeader - sectionPt - common;
  // Room left on a summary page under its charts for the whole table
  // (heading, header row, every row, the tail) at a chart height; undefined
  // when the spec does not say how tall its summary content is.
  const summaryAvail = (chartH: number): number | undefined =>
    spec.layout === "summary" && spec.summaryPt
      ? REPORT_PAGE.textH -
        block.header -
        kpiStripPt(m, lh, kpis) -
        spec.summaryPt(data, block, m, chartH) -
        sectionPt -
        common -
        SUMMARY_FIT_MARGIN_PT
      : undefined;
  const totalRow = spec.totalRow?.(data);
  // The totals row swaps the 1pt hairline for a 1.5pt rule above and a 2pt
  // rule below: 2.5pt more than a body row.
  const totalRowPt =
    totalRow === undefined ? 0 : rowPt(m, lh, totalRow, columns, inner, "Inter-600", false) + 2.5;
  const heights: number[] = [];
  const heightAt = (i: number) => {
    for (let j = heights.length; j <= i; j += 1) heights.push(rowPt(m, lh, sorted[j], columns, inner));
    return heights[i];
  };
  const sortedOffDefault =
    !!sortState && (spec.defaultSort === undefined || sortState.key !== spec.defaultSort.key);
  const loaded = sorted.length;

  const plan = (maxRows?: number): DocPlan<Row> => {
    const rows = limitRows(sorted, spec.limited, maxRows);
    rows.forEach((_, i) => heightAt(i));
    const note = spec.note(data, rows.length, loaded, sortedOffDefault);
    const tailPt = (note ? notePt(m, lh, note) : 0) + totalRowPt;
    // A summary document prints its table under the charts when the whole
    // table fits there (Bandingkan's six rows, a quiet month's claims); an
    // empty table always does. Otherwise the table starts on page 2.
    const bodyPt = rows.reduce((sum, _, i) => sum + heights[i], 0) + tailPt;
    const fitsAt = (chartH: number) => {
      const avail = summaryAvail(chartH);
      return avail !== undefined && bodyPt <= avail;
    };
    // Full-height charts first; the compact height only when that is what
    // lets the table onto page 1.
    const summaryChartH =
      spec.layout === "summary" && rows.length > 0 && !fitsAt(SUMMARY_CHART_H) && fitsAt(SUMMARY_CHART_H_COMPACT)
        ? SUMMARY_CHART_H_COMPACT
        : SUMMARY_CHART_H;
    const tableOnSummaryPage =
      spec.layout === "summary" && (rows.length === 0 || fitsAt(summaryChartH));
    const pages = tableOnSummaryPage
      ? [rows]
      : planTablePages(rows, heights, firstAvail, restAvail, tailPt);
    const pageCount =
      spec.layout === "summary" && !tableOnSummaryPage ? 1 + pages.length : pages.length;
    return { pages, columns, kpis, note, totalRow, tableOnSummaryPage, summaryChartH, pageCount };
  };

  // Pages for a row count the planner has not got the rows for (the
  // document will fetch them): the loaded rows' plan, extended by phantom
  // rows at their mean height.
  const extrapolate = (n: number): number => {
    const full = plan(sorted.length);
    if (sorted.length === 0) return full.pageCount;
    const meanPt = heights.reduce((sum, h) => sum + h, 0) / heights.length;
    const perPage = Math.max(1, Math.floor(restAvail / meanPt));
    return full.pageCount + Math.ceil((n - sorted.length) / perPage);
  };

  return {
    sorted,
    columns,
    plan,
    // Pages the document will have for a chosen row count: exact when the
    // rows are in hand, an extrapolation when the document will fetch more.
    estimate: (maxRows?: number): { pages: number; exact: boolean } => {
      const n = spec.limited ? requestedRows(maxRows) : sorted.length;
      if (!spec.limited || n <= sorted.length) return { pages: plan(maxRows).pageCount, exact: true };
      return { pages: extrapolate(n), exact: false };
    },
  };
};

export type DocPlanner<Data, Row extends Record<string, any>> = ReturnType<
  typeof createDocPlanner<Data, Row>
>;
